import { useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { RiSearchLine } from 'react-icons/ri';

import Box from 'components/ui/box';
import { findManagersMad } from 'services/dashboard.service';

import { DuplaOption } from '../shared/types';
import { expandManagerToDuplaOptions, suggestedOption } from './succession';

// Modal de seleção da DUPLA de liderança que aprecia a próxima etapa (fluxo MAD,
// MOD-06/SUC-04) — espelho de MinhaEquipe/components/SelectGestor mas listando
// opções-par nomeadas ("GESTOR e PARCEIRO") em vez de um único gestor. A opção
// sugerida (SUC-01, marcada pelo backend) vem pré-selecionada; a busca manual
// consulta gestores_mad e expande cada resultado em opção INDIVIDUAL + uma
// opção-par por parceiro (expandManagerToDuplaOptions) — a dupla é opcional,
// então "GESTOR (individual)" aparece mesmo quando há dupla. onSubmit devolve a
// DuplaOption escolhida — a tradução para {evaluatorId, nonEvaluatorId} é do
// chamador (resolveSelectionWrite).

interface SelectDuplaPlayer {
  task: any;
  // Níveis de gestor aceitos na busca manual (mesma semântica do `cod` do SelectGestor).
  cod?: number[];
  // Opções-par já resolvidas pelo backend (buildDuplaOptions) para o nível corrente.
  duplaOptions?: DuplaOption[];
}

interface IProps {
  player: SelectDuplaPlayer;
  closeModal: () => void;
  onSubmit: (option: DuplaOption) => void;
}

// Nome curto legível de um gestor/parceiro: "NOME_GUERRA (CHAVE)" quando houver,
// com degradação graciosa para nome/_id.
function memberLabel(member: DuplaOption['gestor']): string {
  if (!member) return '';

  const name = member.nome_guerra || member.nome || member._id || '';
  const chave = member.chave ? ` (${member.chave})` : '';

  return `${name}${chave}`;
}

// Rótulo da opção-par: "GESTOR e PARCEIRO" ou "GESTOR (individual)" (opção solo —
// presente também para gestor COM dupla, que pode apreciar sozinho).
function duplaLabel(option: DuplaOption): string {
  const gestorLabel = memberLabel(option.gestor);

  if (isDuplaOption(option)) {
    return `${gestorLabel} e ${memberLabel(option.parceiro)}`;
  }

  return `${gestorLabel} (individual)`;
}

// Identidade estável de uma opção (gestor+parceiro) para chave de lista e comparação
// de seleção — evita depender de igualdade referencial entre listas recriadas.
function optionKey(option: DuplaOption | null | undefined): string {
  if (!option) return '';

  return `${option.gestor?._id ?? ''}::${option.parceiro?._id ?? ''}`;
}

// Uma opção é dupla DE FATO quando há parceiro com `_id`; caso contrário é gestor
// individual (solo). Rege tanto o rótulo da opção quanto o texto do botão de
// confirmação ("Confirmar dupla" × "Confirmar gestor").
function isDuplaOption(option: DuplaOption | null | undefined): boolean {
  return Boolean(option?.parceiro?._id);
}

export default function SelectDupla({ player, closeModal, onSubmit }: IProps) {
  ReactModal.setAppElement('#modal');

  const initialOptions = useMemo<DuplaOption[]>(
    () => (Array.isArray(player.duplaOptions) ? player.duplaOptions : []),
    [player.duplaOptions],
  );

  const [isOpen, setIsOpen] = useState(true);
  const [options, setOptions] = useState<DuplaOption[]>(initialOptions);
  const [selected, setSelected] = useState<DuplaOption | null>(() => suggestedOption(initialOptions));
  const [search, setSearch] = useState<string>('');
  const [searching, setSearching] = useState(false);

  // Busca manual em gestores_mad: cada gestor achado é expandido em opções-par e
  // acrescentado à lista (sem duplicar as já presentes). Nenhuma opção da busca vem
  // sugerida — a sugestão é exclusiva de SUC-01.
  async function handleSearch(e?: React.FormEvent) {
    if (e) e.preventDefault();

    if (!search.trim()) return;

    try {
      setSearching(true);

      const result = await findManagersMad(search, player.cod);

      const expanded = (Array.isArray(result) ? result : []).flatMap((manager: any) =>
        expandManagerToDuplaOptions(manager),
      );

      setOptions(prev => {
        const seen = new Set(prev.map(optionKey));
        const merged = [...prev];

        expanded.forEach(option => {
          const key = optionKey(option);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(option);
          }
        });

        return merged;
      });
    } finally {
      setSearching(false);
    }
  }

  function afterOpenModal() {}

  function handleAfterClose() {
    closeModal();
  }

  const selectedKey = optionKey(selected);
  const confirmLabel = isDuplaOption(selected) ? 'Confirmar dupla' : 'Confirmar gestor';

  return (
    <ReactModal
      key={'modal-select-dupla'}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      isOpen={isOpen}
      onAfterOpen={afterOpenModal}
      onAfterClose={handleAfterClose}
      contentLabel="Selecionar dupla de apreciação"
      portalClassName="modal-select-dupla"
      overlayClassName="fixed z-20 top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-center"
      contentElement={() => (
        <Box className="w-[90%] lg:max-w-screen-md">
          <button
            className="absolute w-[33px] h-[33px] flex items-center justify-center -top-4 -right-4 button-close bg-slate-blue rounded-full"
            onClick={() => setIsOpen(false)}
            aria-label="Fechar"
          >
            <IoClose size={23} className="text-white leading-none block m-0 p-0" />
          </button>

          <div className="mx-auto relative container overflow-y-auto overflow-x-hidden max-h-[85vh]">
            <div className="block mb-8">
              <h1 className="text-lg uppercase block text-center text-yellow">Confirme a dupla da próxima etapa</h1>
            </div>

            <div className="space-y-6">
              <div className="relative flex-1">
                <span className="text-alice-blue mb-4 block text-base font-thin">
                  Dupla responsável por apreciar a realização
                </span>

                <div className="flex space-x-3">
                  <form onSubmit={handleSearch} className="pl-1 flex-1">
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Buscar por nome ou matrícula"
                      className={`text-white w-full bg-maastricht-blue py-2 px-4 rounded-md leading-none text-sm font-light`}
                    />
                  </form>

                  <button onClick={() => handleSearch()} aria-label="Buscar gestor">
                    <RiSearchLine className="text-grey-dark" size={25} />
                  </button>
                </div>

                <span className="text-yellow text-xs mt-4 pl-1 block">
                  Esta dupla é responsável por realizar a apreciação desta entrega? Caso não seja ela, você pode buscar
                  outra dupla acima. A dupla age como um: gestor e parceiro apreciam em conjunto.
                </span>
              </div>

              {searching && (
                <div className="flex-1 bg-rich-black bg-opacity-80 p-8 rounded-3xl">
                  <p className="animate-pulse text-yellow">Recuperando informações...</p>
                </div>
              )}

              {!searching && !!options.length && (
                <div className="flex-1 bg-rich-black bg-opacity-80 p-5 lg:p-8 rounded-3xl max-h-[40vh] overflow-y-auto">
                  <div className="space-y-4">
                    {options.map((option, index) => {
                      const key = optionKey(option);
                      const isSelected = key === selectedKey;

                      return (
                        <button
                          key={`${key}-${index}`}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => setSelected(option)}
                          className={`${
                            isSelected
                              ? 'ring-2 ring-slate-blue bg-slate-blue bg-opacity-40'
                              : 'ring-1 ring-prussian-blue hover:ring-slate-blue'
                          } cursor-pointer w-full block rounded-md px-5 py-4 text-left transition-colors`}
                        >
                          <span className="text-base font-semibold text-white block">{duplaLabel(option)}</span>
                          {option.suggested ? (
                            <span className="text-electric-blue text-xs uppercase">Sugerida</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!searching && !options.length && (
                <div className="flex-1 bg-rich-black bg-opacity-80 p-8 rounded-3xl text-center text-white font-light">
                  Nenhuma dupla disponível. Experimente buscar por nome e sobrenome ou matrícula.
                </div>
              )}

              <div className="flex justify-center">
                <button
                  disabled={!selected}
                  onClick={() => {
                    if (selected) onSubmit(selected);
                  }}
                  className={`mx-auto mb-2 leading-none disabled:opacity-50 ring-2 ring-slate-blue w-full lg:w-auto flex flex-col lg:flex-row items-center bg-slate-blue text-center text-white rounded py-3 px-4`}
                >
                  <p className="font-bold text-sm leading-none uppercase">{confirmLabel}</p>
                </button>
              </div>
            </div>
          </div>
        </Box>
      )}
    />
  );
}
